<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Calendar } from "$lib/components/ui/calendar"
  import type { _response } from "$lib/data/type/_response"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { CalendarDate, getLocalTimeZone, parseDate, today, type DateValue } from "@internationalized/date"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import { createQuery } from "@tanstack/svelte-query"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import { type Event, EventEmpty } from "./_types"

  const prefix = $derived(page.params.prefix ?? "0")

  const initialDate = today(getLocalTimeZone())

  let month = $state(new CalendarDate(initialDate.year, initialDate.month, 1))

  let selected = $state<Event>(EventEmpty)
  const detailDialog = useDialog()

  const query = createQuery(() => ({
    queryKey: ["event", {
      prefix,
      year: month.year,
      month: month.month
    }],
    queryFn: async () => {
      const response = await httpClient.get<_response<Event[]>>(`/api/${prefix}/info/events`, {
        params: {
          date_start: month.toString(),
          date_end: month.add({ months: 1 }).subtract({ days: 1 }).toString()
        }
      })
      return response.data.result ?? []
    }
  }))

  const dates = $derived(query.data?.map((event) => event.date.toDate()) ?? [])

  function handleDetail(event: Event) {
    selected = event
    detailDialog.setOpen(true)
  }

  function handleMonthChange(value: DateValue) {
    const date = parseDate(value.toString())

    if (date.year === month.year && date.month === month.month) return

    month = date.set({ day: 1 })
  }

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Event</h1>
      </div>
      <div></div>
    </div>
    <Calendar
      type="multiple"
      class="w-full [--cell-width:calc(100%/7)]"
      placeholder={month}
      value={dates}
      disabled
      disableDaysOutsideMonth
      onPlaceholderChange={handleMonthChange}/>
  </nav>
  <div class="min-h-full">
    {#if query.isFetching}
      <div class="w-full px-3 py-2 text-xs">
        <div class="h-4 w-56 animate-pulse rounded bg-accent"></div>
        <div class="mt-1 h-4 w-36 animate-pulse rounded bg-accent"></div>
      </div>
    {:else if query.isError}
      <div class="flex flex-col items-center gap-2 px-3 py-4 text-center text-xs">
        <div>Terjadi kesalahan, Silahkan coba sesaat lagi</div>
        <Button
          variant="outline"
          class="h-8 rounded-full"
          onclick={() => void query.refetch()}>
          Muat Ulang
        </Button>
      </div>
    {:else if !query.data?.length}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {:else}
      {#each query.data as event (event.id)}
        <Button
          variant="ghost"
          class="block h-auto w-full rounded-none px-3 py-2 text-start text-sm font-normal whitespace-normal"
          onclick={() => handleDetail(event)}>
          <div>{event.name}</div>
          <div class="font-light">
            <span class="tabular-nums tracking-tight">
              {event.date.toDateFormat("dd/MM/yyyy")}
            </span>
            <span class="mx-2">-</span>
            <span>{event.pic_name}</span>
          </div>
        </Button>
      {/each}
    {/if}
  </div>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={selected}/>
